<template>
    <div >
         <v-card :disabled="isProcessing" :loading="isProcessing">
            <template v-slot:progress>
                <v-progress-linear
                    absolute
                    color="success"
                    height="4"
                    indeterminate
                ></v-progress-linear>
            </template>

            <v-card-text v-show="showPanel">
                <v-form
                    @submit.prevent="store"
                    ref="inputForm"
                    v-model="validRawData"
                >
                    <v-row>
                        <v-col v-if="rawData.import_type?.slug !== 'email'" cols="12" sm="3">
                            <TPContactsAutocomplete
                                v-model="rawData.company_contact_id"
                                :defaults="rawData.company_contact"
                                :show-panel="showPanel"
                                @processing="handleProcessing"
                                @processed="handleProcessed"
                            ></TPContactsAutocomplete>
                        </v-col>
                        <v-col cols="12" sm="3">
                            <TPDatePicker
                                label="Date"
                                v-model="rawData.date"
                                :key="rawData.dataKey || 0"
                                :rules="[validations.required()]"
                                @input="handleDateInput"
                            ></TPDatePicker>
                        </v-col>
                        <v-col cols="12" sm="3">
                            <TPDatePicker
                                label="Expire Date"
                                :key="rawData.expireDataKey || 0"
                                v-model="rawData.expire_date"
                                :rules="[validations.required()]"
                            ></TPDatePicker>
                        </v-col>
                        <v-col cols="12" sm="3">
                          <v-select
                            v-model="rawData.import_type"
                            :items="importTypes"
                            item-text="name"
                            item-value="id"
                            return-object
                            :label="$t('GENERAL.LABELS.IMPORT_TYPE')"
                            :disabled="isProcessing || id !== undefined"
                            @change="handleImportTypeChange"
                          />
                        </v-col>
                        <v-col cols="12">
                            <template v-if="rawData.import_type?.slug === 'csv-file-template'">
                              <v-btn
                                color="primary"
                                @click="downloadCsvTemplate()"
                              >
                                {{ $t('GENERAL.LABELS.DOWNLOAD_TEMPLATE') }}
                              </v-btn>
                              <v-btn
                                color="primary"
                                class="ml-2"
                                @click="$refs['csv-uploader'].click()"
                              >
                                {{ $t('GENERAL.LABELS.CHOOSE_FILE') }}
                              </v-btn>
                              <span class="ml-2 filename">
                                {{ rawData.file?.name }}
                              </span>
                              <span v-if="fileError" class="ml-2 filename text-danger">
                                {{ $t('GENERAL.LABELS.PLEASE_CHOOSE_FILE') }}
                              </span>
                              <input
                                ref="csv-uploader"
                                class="d-none"
                                type="file"
                                accept=".csv"
                                @change="uploadFile"
                              />
                            </template>
                            <template v-if="rawData.import_type?.slug === 'email'">
                              <div class="subtitle-2 mb-2">Pending emails</div>
                              <v-list
                                v-if="pendingEmailOffers.length"
                                two-line
                                dense
                                class="email-offer-list"
                              >
                                <v-list-item
                                  v-for="item in pendingEmailOffers"
                                  :key="item.id"
                                  link
                                  :disabled="isProcessing || id !== undefined"
                                  :input-value="selectedEmailOfferId === item.id"
                                  color="primary"
                                  @click="handleEmailOfferChange(item.id)"
                                >
                                  <v-list-item-content>
                                    <v-list-item-title>
                                      {{ item.subject || '(no subject)' }}
                                      <v-chip
                                        v-if="item.is_new_contact"
                                        x-small
                                        color="warning"
                                        class="ml-2"
                                      >
                                        New contact
                                      </v-chip>
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                      {{ emailOfferSubtitle(item) }}
                                    </v-list-item-subtitle>
                                  </v-list-item-content>
                                  <v-list-item-action>
                                    <v-list-item-action-text>
                                      {{ formatEmailDate(item.created_at) }}
                                    </v-list-item-action-text>
                                  </v-list-item-action>
                                  <v-list-item-action>
                                    <v-btn
                                      icon
                                      small
                                      :disabled="isProcessing || id !== undefined"
                                      @click.stop="askDeleteEmailOffer(item)"
                                    >
                                      <v-icon small color="red">mdi-delete</v-icon>
                                    </v-btn>
                                  </v-list-item-action>
                                </v-list-item>
                              </v-list>
                              <div v-else class="grey--text mb-2">
                                No pending emails
                              </div>
                              <span v-if="fileError" class="filename text-danger">
                                Please choose an email
                              </span>
                            </template>
                            <template v-if="rawData.import_type?.slug === 'file'">
                              <v-btn
                                color="primary"
                                @click="$refs['file-uploader'].click()"
                              >
                                {{ $t('GENERAL.LABELS.CHOOSE_FILE') }}
                              </v-btn>
                              <span class="ml-2 filename">
                                {{ selectedFileName }}
                              </span>
                              <span v-if="fileError" class="ml-2 filename text-danger">
                                {{ $t('GENERAL.LABELS.PLEASE_CHOOSE_FILE') }}
                              </span>
                              <input
                                ref="file-uploader"
                                class="d-none"
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.ods,.csv,.txt"
                                @change="uploadFile"
                              />
                            </template>
                            <v-textarea
                                v-if="showOfferContent"
                                :class="{ 'mt-4': rawData.import_type?.slug === 'file' || rawData.import_type?.slug === 'email' }"
                                :rows="offerContentRows"
                                name="input-7-1"
                                label="Offer Content"
                                prepend-icon="mdi-text"
                                :disabled="isProcessing"
                                :readonly="rawData.import_type?.slug === 'email'"
                                v-model="rawData.raw"
                                :rules="[validations.required()]"
                                @paste="onPaste"
                            ></v-textarea>
                            <div v-if="rawData.import_type?.slug === 'email' && selectedEmail" class="mt-4">
                              <div class="subtitle-2 mb-2">Original email</div>
                              <iframe
                                v-if="selectedEmail.raw_content_html"
                                class="email-preview"
                                sandbox=""
                                :srcdoc="selectedEmail.raw_content_html"
                              ></iframe>
                              <pre v-else class="email-preview-plain">{{ selectedEmail.raw_content_plain }}</pre>
                              <div v-if="selectedEmail.attachments?.length" class="mt-3">
                                <div class="subtitle-2 mb-1">Attachments</div>
                                <div class="d-flex flex-wrap">
                                  <div
                                    v-for="attachment in selectedEmail.attachments"
                                    :key="attachment.id"
                                    class="attachment-item mr-4 mb-2"
                                  >
                                    <img
                                      v-if="attachmentPreviews[attachment.id]"
                                      :src="attachmentPreviews[attachment.id]"
                                      :alt="attachment.file_name"
                                      class="attachment-preview"
                                    />
                                    <v-btn
                                      text
                                      small
                                      color="primary"
                                      class="px-0"
                                      @click="downloadAttachment(attachment)"
                                    >
                                      {{ attachment.file_name }}
                                    </v-btn>
                                  </div>
                                </div>
                              </div>
                            </div>
                        </v-col>
                        <v-col cols="12">
                            <v-textarea
                                rows=2
                                name="input-7-1"
                                label="Notes"
                                prepend-icon="mdi-text"
                                :disabled="isProcessing"
                                v-model="rawData.notes"
                            ></v-textarea>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>

            <div>
            <v-card-actions v-if="showPanel">

                <v-switch
                    v-show="rawData.import_type?.slug !== 'file' && rawData.import_type?.slug !== 'email'"
                    v-model="isAutoProcessEnabled"
                    hide-details
                    label="Auto process on paste"
                    @change="handleIsAutoProcessEnabledChange($event)"
                ></v-switch>
                <v-switch
                    v-show="rawData.import_type?.slug === 'text'"
                    v-model="isTableRecognitionEnabled"
                    class="ml-7"
                    hide-details
                    label="Enable table recognition"
                    @change="handleIsTableRecognitionEnabledChange($event)"
                ></v-switch>
                <v-spacer></v-spacer>
                   <v-btn
                    v-if="updateId"
                    color="warning"
                    v-on:click="back()"
                    >Back</v-btn
                >
                <v-btn color="primary" @click="store">Process</v-btn>
            </v-card-actions>
            </div>

        </v-card>
           <TPConfirm
            v-model="showConfirmBack"
            content="All current changes will be lost."
            @confirm="confirmBack"
            ></TPConfirm>
           <TPConfirm
            v-model="showConfirmDeleteEmail"
            content="This email will be removed from pending and will not be imported."
            @confirm="confirmDeleteEmail"
            ></TPConfirm>
    </div>


</template>

<script>
import { mapGetters } from "vuex";
import { PROCESS_VENDOR_OFFER_SET, EXTRACT_VENDOR_OFFER_SET_FILE } from "@/store/vendorOfferSet.module";
import {
    GET_PENDING_EMAIL_OFFERS,
    GET_EMAIL_OFFER,
    DOWNLOAD_EMAIL_OFFER_ATTACHMENT,
    FETCH_EMAIL_OFFER_ATTACHMENT,
    DELETE_EMAIL_OFFER
} from "@/store/emailOffer.module";
import validations from "@/views/partials/form/Validations.vue";
import TPDatePicker from "@/views/partials/form/DatePicker.vue";
import TPConfirm from "@/views/partials/dialogs/Confirm.vue";
import parseHtmlTableService from "@/common/parseHtmlTable.service.js";
import userStateService from "@/common/userState.service.js";
import moment from "moment";
import { RESET_DATA } from "@/store/vendorOfferSet.module";
import { GET_VENDOR_OFFER_SET_IMPORT_TYPES } from "@/store/vendorOfferSetImportType.module";

import TPContactsAutocomplete from "./ContactsAutocomplete";
import { SHOW_SNACK } from "@/store/snack.module";
import { API_URL, API_PATH } from "@/common/config";

export default {
    props: ["id", "stored","rawData","isAutoProcess","updateId"],
    components: {
        TPDatePicker,
        TPConfirm,
        TPContactsAutocomplete
    },
    computed: {
        ...mapGetters([
            "fieldChange"
        ]),
        showOfferContent() {
            return this.rawData.import_type?.slug === 'text'
                || (this.rawData.import_type?.slug === 'file' && !!this.rawData.raw)
                || (this.rawData.import_type?.slug === 'email' && !!this.rawData.raw);
        }
    },
    watch: {
        stored(value) {
            if (value && this.rawData.import_type?.slug === 'email' && !this.id) {
                this.resetEmailForm();
            }
        },
        isAutoProcess(value){
            this.isAutoProcessEnabled = value;
        },
        id() {
            this.useTextImportTypeForEmailEdit();
        },
        'rawData.import_type'() {
            this.useTextImportTypeForEmailEdit();
        },

    },
    beforeDestroy() {
        this.clearAttachmentPreviews();
    },
    mounted(){
       this.fetchImportTypes();

        setTimeout(x=>{
            this.offerContentRows = parseInt(( document.getElementsByTagName('body')[0].offsetHeight-509)/22.23)
        },100)
    },
    data() {
        return {
            offerContentRows: 12,
            validations: validations,
            showPanel: true,
            processed: false,
            expireDatePickerKey: 0,
            validRawData: false,
            showConfirm: false,
            isProcessing: false,
            showConfirmBack:false,
            showConfirmDeleteEmail: false,
            emailOfferToDelete: null,
            selectedFileName: '',
            selectedEmailOfferId: null,
            selectedEmail: null,
            attachmentPreviews: {},
            pendingEmailOffers: [],
            fileError: false,
            importTypes: [],
            isAutoProcessEnabled:
              userStateService.getState(
                'add-vendor-offer.auto-process-enabled',
                true
              ),
            isTableRecognitionEnabled:
              userStateService.getState(
                'add-vendor-offer.table-recognition-enabled',
                true
              ),
        };
    },
    methods: {
        fetchImportTypes() {
          this.$store.dispatch(GET_VENDOR_OFFER_SET_IMPORT_TYPES)
            .then(response => {
              this.importTypes = response;

              if (!this.rawData.import_type_id && !this.id) {
                let importType = this.importTypes.find(importType => importType.slug === 'email')
                  || this.importTypes.find(importType => importType.slug === 'text');

                if (importType) {
                  this.rawData.import_type_id = importType.id;
                  this.rawData.import_type = importType;
                }
              }

              this.useTextImportTypeForEmailEdit();

              if (this.rawData.import_type?.slug === 'email' && !this.id) {
                this.fetchPendingEmailOffers();
              }
            })
            .catch(error => {
              this.$store.dispatch(SHOW_SNACK, {
                type: 'error',
                message: this.$t('GENERAL.UNEXPECTED_ERROR')
              });
            });
        },

        downloadCsvTemplate() {
          location.href = `${API_URL}${API_PATH}vendorOfferSets/download-csv-import-template`;
        },

        useTextImportTypeForEmailEdit() {
          if (!this.id || this.rawData.import_type?.slug !== 'email') {
            return;
          }

          const textType = this.importTypes.find(importType => importType.slug === 'text');
          if (!textType) {
            return;
          }

          this.rawData.import_type_id = textType.id;
          this.rawData.import_type = textType;
        },

        resetEmailForm() {
          const savedId = this.selectedEmailOfferId || this.rawData.email_offer_id;

          this.clearAttachmentPreviews();
          this.selectedEmailOfferId = null;
          this.selectedEmail = null;
          this.rawData.email_offer_id = null;
          this.rawData.raw = null;
          this.rawData.lines = [];
          this.rawData.company_contact_id = null;
          this.rawData.company_contact = null;
          this.rawData.notes = null;
          this.rawData.file = null;
          this.rawData.date = moment().format("YYYY-MM-DD");
          this.rawData.expire_date = moment().add(7, "days").format("YYYY-MM-DD");
          this.rawData.dataKey = (this.rawData.dataKey || 0) + 1;
          this.rawData.expireDataKey = (this.rawData.expireDataKey || 0) + 1;
          this.fileError = false;

          if (savedId) {
            this.pendingEmailOffers = this.pendingEmailOffers.filter(offer => offer.id !== savedId);
          }

          this.$store.dispatch(RESET_DATA);
          this.fetchPendingEmailOffers();
        },

        fetchPendingEmailOffers() {
          this.$store.dispatch(GET_PENDING_EMAIL_OFFERS)
            .then(items => {
              this.pendingEmailOffers = items || [];
            })
            .catch(() => {
              this.$store.dispatch(SHOW_SNACK, {
                type: 'error',
                message: this.$t('GENERAL.UNEXPECTED_ERROR')
              });
            });
        },

        emailOfferSubtitle(item) {
          const contact = item.company_contact;
          const vendor = contact
            ? (contact.company_name || contact.name)
            : null;

          return vendor
            ? `${item.from} — ${vendor}`
            : item.from;
        },

        formatEmailDate(date) {
          if (!date) {
            return '';
          }

          return moment(date).format('DD.MM.YYYY HH:mm');
        },

        handleEmailOfferChange(id) {
          this.fileError = false;

          if (id === this.selectedEmailOfferId && this.selectedEmail) {
            return;
          }

          this.clearAttachmentPreviews();
          this.selectedEmailOfferId = id || null;
          this.rawData.email_offer_id = id || null;
          this.selectedEmail = null;
          this.rawData.raw = null;
          this.rawData.lines = [];
          this.rawData.company_contact_id = null;
          this.rawData.company_contact = null;

          if (!id) {
            return;
          }

          const pending = this.pendingEmailOffers.find(item => item.id === id);
          if (pending?.company_contact_id) {
            this.rawData.company_contact_id = pending.company_contact_id;
            this.rawData.company_contact = pending.company_contact;
          }

          this.isProcessing = true;
          this.$store.dispatch(GET_EMAIL_OFFER, id)
            .then(email => {
              this.selectedEmail = email;
              this.rawData.raw = email.extracted_offers_text;
              this.rawData.email_offer_id = email.id;
              this.rawData.company_contact_id = email.company_contact_id || null;
              this.rawData.company_contact = email.company_contact || null;
              this.loadAttachmentPreviews(email);
            })
            .catch((response) => {
              this.handleException(response);
            })
            .finally(() => {
              this.isProcessing = false;
            });
        },

        isImageAttachment(attachment) {
          const type = String(attachment?.file_type || '').toLowerCase();
          if (type.startsWith('image/')) {
            return true;
          }

          return /\.(jpe?g|png|gif|webp|bmp|heic|heif|tiff?)$/i.test(attachment?.file_name || '');
        },

        clearAttachmentPreviews() {
          Object.values(this.attachmentPreviews).forEach(url => {
            window.URL.revokeObjectURL(url);
          });
          this.attachmentPreviews = {};
        },

        loadAttachmentPreviews(email) {
          const offerId = email.id;

          (email.attachments || []).filter(this.isImageAttachment).forEach(attachment => {
            this.$store.dispatch(FETCH_EMAIL_OFFER_ATTACHMENT, {
              emailOfferId: offerId,
              attachmentId: attachment.id
            }).then(blob => {
              if (this.selectedEmailOfferId !== offerId) {
                return;
              }

              this.$set(this.attachmentPreviews, attachment.id, window.URL.createObjectURL(blob));
            }).catch(() => {});
          });
        },

        downloadAttachment(attachment) {
          this.$store.dispatch(DOWNLOAD_EMAIL_OFFER_ATTACHMENT, {
            emailOfferId: this.selectedEmailOfferId,
            attachmentId: attachment.id,
            fileName: attachment.file_name
          }).catch((response) => {
            this.handleException(response);
          });
        },

        askDeleteEmailOffer(item) {
          this.emailOfferToDelete = item;
          this.showConfirmDeleteEmail = true;
        },

        confirmDeleteEmail() {
          const item = this.emailOfferToDelete;
          this.emailOfferToDelete = null;

          if (!item) {
            return;
          }

          this.isProcessing = true;
          this.$store.dispatch(DELETE_EMAIL_OFFER, item.id)
            .then(() => {
              this.pendingEmailOffers = this.pendingEmailOffers.filter(offer => offer.id !== item.id);

              if (this.selectedEmailOfferId === item.id) {
                this.clearAttachmentPreviews();
                this.selectedEmailOfferId = null;
                this.selectedEmail = null;
                this.rawData.email_offer_id = null;
                this.rawData.raw = null;
                this.rawData.company_contact_id = null;
                this.rawData.company_contact = null;
              }
            })
            .catch((response) => {
              this.handleException(response);
            })
            .finally(() => {
              this.isProcessing = false;
            });
        },

        uploadFile(event) {
          let file = event.target.files[0];

          if(!file){
              return;
          }

          this.rawData.file = file;

          this.fileError = false;

          this.$forceUpdate();

          event.target.value = '';

          if (this.rawData.import_type?.slug === 'file') {
              this.extractFile(file);
              return;
          }

          if (this.isAutoProcessEnabled) {
                setTimeout(() => {
                    this.store();
                }, 4);
            }
        },

        extractFile(file) {
          this.selectedFileName = file.name;
          this.isProcessing = true;
          this.$emit("processing");

          this.$store
            .dispatch(EXTRACT_VENDOR_OFFER_SET_FILE, file)
            .then((document) => {
              this.rawData.raw = document.text;
              // Which line is an offer and which is a heading is already known from the
              // file layout. Passing it on lets the backend skip a model call, and it is
              // ignored there as soon as the text is edited by hand.
              this.rawData.lines = document.lines;
              this.rawData.file = null;
            })
            .catch((response) => {
              this.handleException(response);
            })
            .finally(() => {
              this.isProcessing = false;
            });
        },

        onPaste(event) {
          if (this.rawData.import_type?.slug !== 'text') {
            return;
          }

          const clipHTMLText = event.clipboardData.getData('text/html');

          setTimeout(() => {
            if (clipHTMLText && this.isTableRecognitionEnabled) {
              let parsedData = parseHtmlTableService.process(clipHTMLText);

              if (parsedData) {
                this.rawData.raw = parsedData;
              }
            }

            if (this.isAutoProcessEnabled) {
                setTimeout(() => {
                    this.store();
                }, 4);
            }
          }, 50);
        },
        handleDateInput(date) {
            this.rawData.expire_date = moment(date)
                .add(7, "days")
                .format("YYYY-MM-DD");
            this.rawData.expireDataKey++;
        },
        handleImportTypeChange(importType) {
          this.rawData.import_type_id = importType.id;
          this.rawData.file = null;
          this.rawData.lines = [];
          this.rawData.email_offer_id = null;
          this.selectedFileName = '';
          this.clearAttachmentPreviews();
          this.selectedEmailOfferId = null;
          this.selectedEmail = null;
          this.fileError = false;

          if (importType.slug === 'email') {
            this.fetchPendingEmailOffers();
          }
        },
        store() {
            this.$refs.inputForm.validate();
            this.processed = false;
            if (!this.validRawData) {
                return;
            }

            if (this.rawData.import_type?.slug === 'csv-file-template' && !this.rawData.file) {
              this.fileError = true;

              this.$forceUpdate();

              return;
            }

            if (this.rawData.import_type?.slug === 'file' && !this.rawData.raw) {
              this.fileError = true;

              this.$forceUpdate();

              return;
            }

            if (this.rawData.import_type?.slug === 'email' && (!this.rawData.raw || !this.rawData.company_contact_id)) {
              this.fileError = true;

              this.$forceUpdate();

              return;
            }

            if (this.processed && !this.stored) {
                this.showConfirm = true;
                return;
            }
            this.$store.dispatch(RESET_DATA);

            this.isProcessing = true;
            this.$emit("processing");

            this.$store
                .dispatch(PROCESS_VENDOR_OFFER_SET, this.rawData)
                .then((data) => {
                  this.rawData.raw = data.data.set.text;

                    this.processed = true;
                    this.$emit("processed");
                    this.$nextTick(() => {
                        // this.showPanel = false;
                    });
                })
                .catch((response) => {
                      this.handleException(response)
                      this.$emit("processFailed");
                })
                .finally(() => {
                    this.isProcessing = false;
                });
        },
        confirmProcess() {
            this.processed = false;
            this.store();
        },
        handleProcessing() {
            this.isProcessing = true;
        },
        handleProcessed() {
            this.isProcessing = false;
        },
        handleIsAutoProcessEnabledChange(value) {
          userStateService.setState(
            'add-vendor-offer.auto-process-enabled',
            value
          );
        },
        handleIsTableRecognitionEnabledChange(value) {
          userStateService.setState(
            'add-vendor-offer.table-recognition-enabled',
            value
          );
        },
        back(){
            if(this.fieldChange){
                this.showConfirmBack = true;
            }else{
                this.confirmBack();
            }
        },
        confirmBack(){
             this.$router.push({ name: 'list-offers' })
        }
    }
};
</script>

<style lang="scss" scoped>
  .filename {
    font-size: 16px;
  }

  .email-offer-list {
    max-height: 280px;
    overflow-y: auto;
    border: 1px solid #e0e0e0;
    padding: 0;
  }

  .email-preview {
    width: 100%;
    height: 360px;
    border: 1px solid #e0e0e0;
    background: #fff;
  }

  .attachment-preview {
    display: block;
    max-width: 240px;
    max-height: 180px;
    object-fit: contain;
    border: 1px solid #e0e0e0;
    background: #fff;
  }

  .email-preview-plain {
    max-height: 360px;
    overflow: auto;
    white-space: pre-wrap;
    font-size: 13px;
    padding: 12px;
    border: 1px solid #e0e0e0;
    background: #fff;
  }
</style>
